import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { animate, motion, useInView } from "framer-motion";
import { Star } from "lucide-react";
import Reveal, { SectionHead } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_STATS = {
  competition_label: "Colo-Colo · Copa Chile 2026",
  updated_label: "Actualizado al 24 Oct 2026 — 4 Partidos jugados · 3 Titularidades · 231 Minutos · 0 Goles · 0 Asistencias · 1 Tarjeta amarilla (todas en Copa Chile 2026)",
  matches: 4,
  starts: 3,
  minutes: 231,
  goals: 0,
  assists: 0,
  yellow_cards: 1,
};

const MATCH_LOG = [
  { n: 1, minutes: 44, note: "Ingresó en el segundo tiempo", starter: false },
  { n: 2, minutes: 75, note: "Titular", starter: true },
  { n: 3, minutes: 67, note: "Titular", starter: true },
  { n: 4, minutes: 45, note: "Titular · Partido suspendido por lluvia, se completará en otra fecha", starter: true },
];

function Counter({ to, testId }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const target = typeof to === "number" && !Number.isNaN(to) ? to : 0;
    const controls = animate(0, target, {
      duration: 1.6,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to]);

  return (
    <span ref={ref} data-testid={testId} className="font-display text-4xl sm:text-5xl font-black leading-none">
      {val.toLocaleString("es-CL")}
    </span>
  );
}

function Bar({ label, pct, testId }) {
  return (
    <div>
      <div className="flex justify-between font-tech text-xs uppercase tracking-[0.2em] text-neutral-400 mb-2">
        <span>{label}</span>
        <span data-testid={testId} className="text-[#d4af37]">{pct}%</span>
      </div>
      <div className="h-[3px] bg-neutral-800 w-full overflow-hidden">
        <motion.div
          className="h-full bg-[#d4af37]"
          initial={{ width: 0 }}
          whileInView={{ width: `${pct}%` }}
          viewport={{ once: true }}
          transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
        />
      </div>
    </div>
  );
}

function MiniStat({ label, value, testId }) {
  return (
    <div className="group border border-neutral-800 bg-[#0f0f0f] p-6 hover:border-[#d4af37]/60 hover:bg-[#141414] transition-colors">
      <Counter to={value} testId={testId} />
      <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-neutral-500 mt-3">{label}</p>
    </div>
  );
}

export default function Estadisticas() {
  const [stats, setStats] = useState(DEFAULT_STATS);

  useEffect(() => {
    axios.get(`${API}/stats`).then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <section id="estadisticas" data-testid="estadisticas-section" className="relative py-28 px-6 border-t border-neutral-900">
      <div className="max-w-6xl mx-auto">
        <SectionHead eyebrow="02 — Estadísticas" title="Los números del 42" testId="estadisticas-title" />

        <Reveal className="mt-14">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
            <p className="font-tech text-xs uppercase tracking-[0.35em] text-[#d4af37]" data-testid="stats-competition-label">{stats.competition_label}</p>
            <p className="font-tech text-[10px] uppercase tracking-[0.25em] text-neutral-600" data-testid="stats-updated-label">{stats.updated_label}</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="stats-copa-chile">
            <MiniStat label="Partidos jugados" value={stats.matches} testId="stat-matches-value" />
            <MiniStat label="Titularidades" value={stats.starts} testId="stat-starts-value" />
            <MiniStat label="Minutos jugados" value={stats.minutes} testId="stat-minutes-value" />
            <MiniStat label="Goles" value={stats.goals} testId="stat-goals-value" />
            <MiniStat label="Asistencias" value={stats.assists} testId="stat-assists-value" />
            <MiniStat label="Tarjetas amarillas" value={stats.yellow_cards} testId="stat-yellow-cards-value" />
          </div>
        </Reveal>

        <Reveal delay={0.1} className="mt-4 border border-neutral-800 bg-[#0f0f0f] p-8">
          <p className="font-tech text-xs uppercase tracking-[0.35em] text-neutral-500 mb-6">Registro partido a partido</p>
          <ul className="divide-y divide-neutral-800" data-testid="match-log">
            {MATCH_LOG.map((m) => (
              <li key={m.n} className="flex items-center gap-5 py-4" data-testid={`match-log-${m.n}`}>
                <span className={`font-display text-3xl font-black w-16 shrink-0 ${m.starter ? "text-[#d4af37]" : "text-neutral-500"}`}>
                  {m.minutes}&prime;
                </span>
                <div>
                  <p className="font-tech text-[10px] uppercase tracking-[0.25em] text-neutral-500">Partido {m.n}</p>
                  <p className="text-sm text-neutral-300 mt-0.5">{m.note}</p>
                </div>
              </li>
            ))}
          </ul>
        </Reveal>

        <div className="mt-10 grid lg:grid-cols-2 gap-4">
          <Reveal className="border border-neutral-800 bg-[#0f0f0f] p-8 flex flex-col justify-between min-h-[260px]">
            <div>
              <p className="font-tech text-xs uppercase tracking-[0.35em] text-[#d4af37]">Mundial Sub-17 FIFA · Qatar 2025</p>
              <div className="mt-6 flex items-start gap-10">
                <div>
                  <Counter to={3} testId="stat-wc-matches-value" />
                  <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-neutral-500 mt-2">Partidos</p>
                </div>
                <div>
                  <Counter to={98} testId="stat-wc-minutes-value" />
                  <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-neutral-500 mt-2">Minutos</p>
                </div>
              </div>
            </div>
            <div className="mt-8 border border-[#d4af37]/50 bg-[#d4af37]/5 px-5 py-4 flex items-center gap-4" data-testid="stat-wc-mvp-card">
              <Star size={22} className="fill-[#d4af37] text-[#d4af37] shrink-0" />
              <div>
                <p className="font-display text-lg font-extrabold uppercase leading-tight">Jugador del Partido</p>
                <p className="text-xs text-neutral-400 mt-1">Victoria de Chile 2–1 sobre Canadá</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="border border-neutral-800 bg-[#0f0f0f] p-8 flex flex-col justify-center gap-6">
            <p className="font-tech text-xs uppercase tracking-[0.35em] text-neutral-500">Rendimiento · Mundial Sub-17</p>
            <Bar label="Pases completados" pct={79} testId="stat-pass-value" />
            <Bar label="Duelos defensivos ganados" pct={69} testId="stat-duels-value" />
            <Bar label="Pases completados en último tercio" pct={63} testId="stat-final-third-value" />
            <Bar label="Acciones totales logradas" pct={65} testId="stat-actions-value" />
          </Reveal>
        </div>
      </div>
    </section>
  );
}
